import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Chart, ScriptableContext, TooltipItem } from 'chart.js';
import { FormatType } from '../../enums/format-type.enum';
import { FormatHelper } from '../../helpers/format.helper';
import { TimelineData, TimelineSelection } from './alert-timeline.types';

/**
 * Stacked bars over time with a drag-select: clicking a bar or dragging across bars emits the span they cover,
 * clicking the single selected bar again clears it. Colours and series come from the caller.
 */
@Component({
  selector: 'shared-alert-timeline',
  templateUrl: './alert-timeline.component.html',
  styleUrls: ['./alert-timeline.component.scss'],
  standalone: false,
})
export class AlertTimelineComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() data: TimelineData;
  @Input() selection: TimelineSelection | null = null;
  @Input() selectable = true;
  @Input() height = '140px';
  @Input() showLegend = true;
  @Output() selectionChange = new EventEmitter<TimelineSelection | null>();

  @ViewChild('canvas', { static: true }) canvas: ElementRef<HTMLCanvasElement>;

  private chart: Chart<'bar', number[], string>;
  private dragStart: number | null = null;
  private dragEnd: number | null = null;
  private dragging = false;

  get total(): number {
    return this.data?.buckets.reduce((sum, b) => sum + b.values.reduce((s, v) => s + v, 0), 0) ?? 0;
  }

  get peak(): number {
    return this.data?.buckets.reduce((max, b) => Math.max(max, b.values.reduce((s, v) => s + v, 0)), 0) ?? 0;
  }

  get bucketLabel(): string {
    const ms = this.data?.bucketMilliseconds ?? 0;
    if (ms >= 3600000) {
      const hours = ms / 3600000;
      return `${hours} h`;
    }
    return `${Math.round(ms / 60000)} min`;
  }

  ngAfterViewInit(): void {
    this.createChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.chart && (changes.data || changes.selection)) {
      this.updateChart();
    }
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }

  clearSelection(): void {
    this.selectionChange.emit(null);
  }

  onPointerDown(event: PointerEvent): void {
    if (!this.selectable || !this.chart || !this.data?.buckets.length) {
      return;
    }
    this.dragStart = this.indexAt(event);
    this.dragEnd = this.dragStart;
    this.dragging = true;
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.dragging) {
      return;
    }
    const index = this.indexAt(event);
    if (index !== this.dragEnd) {
      this.dragEnd = index;
      this.paintPreview();
    }
  }

  onPointerUp(event: PointerEvent): void {
    if (!this.dragging) {
      return;
    }
    this.dragging = false;
    this.dragEnd = this.indexAt(event);
    const [from, to] = [Math.min(this.dragStart, this.dragEnd), Math.max(this.dragStart, this.dragEnd)];
    const span = this.spanOf(from, to);
    const current = this.selection;
    const sameSingleBar = from === to && current != null && current.from.getTime() === span.from.getTime() && current.to.getTime() === span.to.getTime();
    this.dragStart = this.dragEnd = null;
    this.selectionChange.emit(sameSingleBar ? null : span);
  }

  onPointerLeave(): void {
    if (this.dragging) {
      this.dragging = false;
      this.dragStart = this.dragEnd = null;
      this.updateChart();
    }
  }

  private indexAt(event: PointerEvent): number {
    const rect = this.canvas.nativeElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const scale = this.chart.scales.x;
    const value = scale.getValueForPixel(x) ?? 0;
    return Math.max(0, Math.min(this.data.buckets.length - 1, Math.round(value)));
  }

  private spanOf(fromIndex: number, toIndex: number): TimelineSelection {
    const from = this.data.buckets[fromIndex].start;
    const to = new Date(this.data.buckets[toIndex].start.getTime() + this.data.bucketMilliseconds);
    return { from, to };
  }

  private isInSelection(index: number): boolean {
    if (this.dragging && this.dragStart != null && this.dragEnd != null) {
      return index >= Math.min(this.dragStart, this.dragEnd) && index <= Math.max(this.dragStart, this.dragEnd);
    }
    if (!this.selection) {
      return true;
    }
    const start = this.data.buckets[index].start.getTime();
    return start >= this.selection.from.getTime() && start < this.selection.to.getTime();
  }

  private labelFor(start: Date): string {
    const spansDays = this.data.buckets.length > 0
      && this.data.buckets[this.data.buckets.length - 1].start.getTime() - this.data.buckets[0].start.getTime() > 24 * 3600000;
    return FormatHelper.format(start, spansDays ? FormatType.ShortDateTime : FormatType.HourMins);
  }

  private createChart(): void {
    const ctx = this.canvas.nativeElement.getContext('2d');
    this.chart = new Chart<'bar', number[], string>(ctx, {
      type: 'bar',
      data: { labels: [], datasets: [] },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 0 },
        events: ['mousemove', 'mouseout'],
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (items: TooltipItem<'bar'>[]): string => (items.length > 0 ? this.labelFor(this.data.buckets[items[0].dataIndex].start) : ''),
              footer: (items: TooltipItem<'bar'>[]): string => (items.length > 0 ? `Total ${this.data.buckets[items[0].dataIndex].values.reduce((s, v) => s + v, 0)}` : ''),
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12 },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            grid: { drawTicks: false },
            border: { display: false, dash: [8, 4] },
            ticks: { precision: 0, maxTicksLimit: 4 },
          },
        },
      },
    });
    this.updateChart();
  }

  private updateChart(): void {
    if (!this.chart || !this.data) {
      return;
    }
    this.chart.data.labels = this.data.buckets.map(b => this.labelFor(b.start));
    this.chart.data.datasets = this.data.series.map((series, seriesIndex) => ({
      label: series.label,
      data: this.data.buckets.map(b => b.values[seriesIndex] ?? 0),
      backgroundColor: (context: ScriptableContext<'bar'>): string => (this.isInSelection(context.dataIndex) ? series.color : series.fadedColor),
      hoverBackgroundColor: series.color,
      borderWidth: 0,
      borderRadius: 2,
      barPercentage: 0.9,
      categoryPercentage: 0.9,
    }));
    this.chart.update();
  }

  private paintPreview(): void {
    this.chart?.update('none');
  }
}
